import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCart } from "../context/CartContext";
import ProductCard from "../components/ProductCard";
import SonIncelenenler, { sonIncelenenEkle } from "../components/SonIncelenenler";
import { markaYolu } from "../data/markalar";
import KarsilastirButonu from "../components/KarsilastirButonu";
import { tamamlayicilar } from "../data/tamamlayicilar";
import {
  kategoriAdi,
  kategoriZinciri,
  useKatalog,
} from "../data/katalog";

const WHATSAPP_NUMARA = "905377981129";
const TELEFON = "+905377981129";

// Çevirisi olmayan özellik (ör. "Ürün kodu BT.FG1401") olduğu gibi gösterilir
const ozellikMetni = (t, kod) =>
  t(`shop.productFeatures.${kod}`, { defaultValue: "" }) || kod;

/** Arama motorları için ürün bilgisi (schema.org/Product) */
const YapisalVeri = ({ urun, kategori }) => {
  useEffect(() => {
    const etiket = document.createElement("script");
    etiket.type = "application/ld+json";
    etiket.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Product",
      name: urun.ad,
      ...(urun.marka && { brand: { "@type": "Brand", name: urun.marka } }),
      image: new URL(urun.gorsel, window.location.origin).href,
      category: kategori,
      sku: urun.id,
    });
    document.head.appendChild(etiket);
    return () => etiket.remove();
  }, [urun, kategori]);
  return null;
};

export default function UrunDetay() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "tr").split("-")[0];
  const { urunler, dizin, adlar, hazir } = useKatalog();
  const { ekle } = useCart();

  const urun = useMemo(() => urunler.find((u) => u.id === id), [urunler, id]);
  const boyutlar = urun?.boyutlar || [];
  const [boyut, setBoyut] = useState("");
  const [eklendi, setEklendi] = useState(false);

  const [buyukGorsel, setBuyukGorsel] = useState(false);
  const [paylasildi, setPaylasildi] = useState(false);

  useEffect(() => {
    setBoyut(boyutlar[0] || "");
    setBuyukGorsel(false);
    if (urun) sonIncelenenEkle(urun.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urun]);

  // Büyük görsel açıkken Esc ile kapanır, arka plan kaymaz
  useEffect(() => {
    if (!buyukGorsel) return;
    const kapat = (e) => e.key === "Escape" && setBuyukGorsel(false);
    window.addEventListener("keydown", kapat);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", kapat);
      document.body.style.overflow = "";
    };
  }, [buyukGorsel]);

  // Paylaş: telefonda sistemin paylaşım menüsü, masaüstünde bağlantıyı kopyalar
  const paylas = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: urun.ad, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setPaylasildi(true);
      setTimeout(() => setPaylasildi(false), 2000);
    } catch {
      /* kullanıcı paylaşımı iptal etti ya da pano erişimi yok */
    }
  };

  const zincir = useMemo(
    () => (urun && dizin ? kategoriZinciri(dizin, urun.kategori) : []),
    [urun, dizin]
  );
  const kategori = urun ? kategoriAdi(adlar, urun.kategori, lang) : "";

  // Tamamlayıcı ürünler: boyaya rulo ve fırça, seramik yapıştırıcıya derz dolgu, matkaba uç …
  const tamamlayanlar = useMemo(() => tamamlayicilar(urun, urunler, dizin), [urun, urunler, dizin]);

  // Benzer ürünler: önce aynı kategori, yetmezse aynı marka (tamamlayıcılarda gösterilenler hariç)
  const benzerler = useMemo(() => {
    if (!urun) return [];
    const gosterilen = new Set(tamamlayanlar.map((u) => u.id));
    const ayniKategori = urunler.filter((u) => u.kategori === urun.kategori && u.id !== urun.id);
    const ayniMarka = urunler.filter(
      (u) => u.marka === urun.marka && u.kategori !== urun.kategori && u.id !== urun.id
    );
    return [...ayniKategori, ...ayniMarka].filter((u) => !gosterilen.has(u.id)).slice(0, 8);
  }, [urunler, urun, tamamlayanlar]);

  // Sekme başlığı ve arama motoru açıklaması
  useEffect(() => {
    if (!urun) return;
    const onceki = document.title;
    document.title = `${urun.ad} — ${t("siteName")}`;
    const meta = document.querySelector('meta[name="description"]');
    const oncekiAciklama = meta?.getAttribute("content");
    meta?.setAttribute(
      "content",
      t("product.metaDescription", { ad: urun.ad, marka: urun.marka, kategori }).replace(/\s{2,}/g, " ")
    );
    return () => {
      document.title = onceki;
      if (meta && oncekiAciklama != null) meta.setAttribute("content", oncekiAciklama);
    };
  }, [urun, kategori, t]);

  if (!hazir) {
    return (
      <section className="container-page section">
        <div className="grid gap-10 lg:grid-cols-2">
          <div className="aspect-square animate-pulse rounded-xl bg-canvas" />
          <div className="space-y-4">
            <div className="h-4 w-24 animate-pulse rounded bg-canvas" />
            <div className="h-8 w-3/4 animate-pulse rounded bg-canvas" />
            <div className="h-24 animate-pulse rounded bg-canvas" />
          </div>
        </div>
      </section>
    );
  }

  if (!urun) {
    return (
      <section className="container-page section text-center">
        <h1 className="page-title">{t("product.notFound")}</h1>
        <Link to="/urunler" className="btn-primary mt-8 inline-flex">
          {t("product.backToCatalog")}
        </Link>
      </section>
    );
  }

  const whatsappMetni = t("product.whatsappText", {
    ad: urun.ad,
    secenek: boyut ? ` (${boyut})` : "",
  });

  const sepeteEkle = () => {
    if (boyutlar.length > 0 && !boyut) return;
    ekle(urun, boyut);
    setEklendi(true);
    setTimeout(() => setEklendi(false), 1500);
  };

  const ozellikler = urun.ozellikler || [];

  return (
    <main>
      <YapisalVeri urun={urun} kategori={kategori} />

      <section className="container-page pb-16 pt-8 sm:pb-20">
        {/* Breadcrumb */}
        <nav
          aria-label="Breadcrumb"
          className="scrollbar-hide mb-8 flex items-center gap-2 overflow-x-auto whitespace-nowrap text-[13px] text-muted"
        >
          <Link to="/urunler" className="transition-colors hover:text-ink">
            {t("products")}
          </Link>
          {zincir.map((s) => (
            <React.Fragment key={s}>
              <span aria-hidden="true" className="text-line">/</span>
              <Link
                to={`/urunler/${s}`}
                state={{ baslik: kategoriAdi(adlar, s, lang) }}
                className="transition-colors hover:text-ink"
              >
                {kategoriAdi(adlar, s, lang)}
              </Link>
            </React.Fragment>
          ))}
        </nav>

        <div className="grid gap-10 lg:grid-cols-[1.05fr_1fr] lg:gap-16">
          {/* Görsel */}
          <button
            type="button"
            onClick={() => setBuyukGorsel(true)}
            aria-label={t("product.zoom")}
            className="group relative block cursor-zoom-in overflow-hidden rounded-2xl border border-line bg-white lg:sticky lg:top-40 lg:self-start"
          >
            <img
              src={urun.gorsel}
              alt={urun.ad}
              className="aspect-square w-full object-contain p-8 transition-transform duration-500 ease-out group-hover:scale-[1.08] sm:p-12"
            />
            <span className="absolute bottom-3 end-3 flex items-center gap-1.5 rounded-full bg-ink/80 px-3 py-1.5 text-[12px] font-medium text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100">
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3.5 w-3.5" aria-hidden="true">
                <circle cx="8.5" cy="8.5" r="5.5" />
                <path d="M12.5 12.5 17 17M8.5 6v5M6 8.5h5" strokeLinecap="round" />
              </svg>
              {t("product.zoom")}
            </span>
          </button>

          {/* Bilgi */}
          <div className="text-start">
            {urun.marka ? (
              <Link
                to={markaYolu(urun.marka)}
                className="text-[12px] font-semibold uppercase tracking-eyebrow text-accent transition-opacity hover:opacity-80"
              >
                {urun.marka}
              </Link>
            ) : (
              urun.yeni && (
                <span className="text-[12px] font-semibold uppercase tracking-eyebrow text-accent">
                  {t("spotlight.newInStore")}
                </span>
              )
            )}

            <h1 className="mt-3 text-[26px] font-semibold leading-[1.2] tracking-[-0.02em] text-ink sm:text-[32px]">
              {urun.ad}
            </h1>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Link
                to={`/urunler/${urun.kategori}`}
                state={{ baslik: kategori }}
                className="inline-flex items-center gap-1.5 rounded-full bg-canvas px-3 py-1 text-[12.5px] text-body transition-colors hover:text-ink"
              >
                {kategori}
              </Link>
              <button
                type="button"
                onClick={paylas}
                className="inline-flex items-center gap-1.5 rounded-full border border-line px-3 py-1 text-[12.5px] text-body transition-colors hover:border-ink/30 hover:text-ink"
              >
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" className="h-3.5 w-3.5" aria-hidden="true">
                  <circle cx="15" cy="4.5" r="2.2" />
                  <circle cx="5" cy="10" r="2.2" />
                  <circle cx="15" cy="15.5" r="2.2" />
                  <path d="M7 9l6-3.4M7 11l6 3.4" />
                </svg>
                {paylasildi ? t("product.linkCopied") : t("product.share")}
              </button>
              <KarsilastirButonu id={urun.id} />
            </div>

            {/* Seçenekler */}
            {boyutlar.length > 0 && (
              <div className="mt-8">
                <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-eyebrow text-muted">
                  {t("product.options")}
                </h2>
                <div className="flex flex-wrap gap-2">
                  {boyutlar.map((b) => (
                    <button
                      key={b}
                      onClick={() => setBoyut(b)}
                      aria-pressed={boyut === b}
                      className={`rounded-md border px-3.5 py-2 text-[13.5px] tabular-nums transition-colors duration-150 ${
                        boyut === b
                          ? "border-ink bg-ink text-white"
                          : "border-line text-body hover:border-ink/40 hover:text-ink"
                      }`}
                    >
                      {b}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Eylemler */}
            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              <button
                onClick={sepeteEkle}
                disabled={boyutlar.length > 0 && !boyut}
                className="btn-primary justify-center disabled:cursor-not-allowed disabled:opacity-50"
              >
                {eklendi ? t("shop.added") : t("shop.addToCart")}
              </button>
              <a
                href={`https://wa.me/${WHATSAPP_NUMARA}?text=${encodeURIComponent(whatsappMetni)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-[#25D366] px-5 py-3 text-[14px] font-semibold text-white transition-colors hover:bg-[#1ebe5b]"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-[18px] w-[18px]" aria-hidden="true">
                  <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.26-.46-2.39-1.47-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.07 2.88 1.21 3.07.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.22 1.36.19 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.12-.27-.2-.57-.35M12.04 21.5h-.01a9.47 9.47 0 0 1-4.83-1.32l-.35-.21-3.59.94.96-3.5-.23-.36a9.46 9.46 0 0 1-1.45-5.04c0-5.23 4.26-9.49 9.5-9.49a9.43 9.43 0 0 1 6.71 2.78 9.43 9.43 0 0 1 2.78 6.72c0 5.23-4.26 9.48-9.49 9.48m8.08-17.56A11.36 11.36 0 0 0 12.04.6C5.74.6.62 5.72.62 12.01c0 2.01.52 3.97 1.52 5.7L.52 23.4l5.83-1.53a11.4 11.4 0 0 0 5.69 1.45h.01c6.29 0 11.41-5.12 11.41-11.41 0-3.05-1.19-5.91-3.34-8.07" />
                </svg>
                {t("product.askWhatsapp")}
              </a>
            </div>
            <a
              href={`tel:${TELEFON}`}
              className="mt-3 flex items-center justify-center gap-2 rounded-md border border-line px-5 py-3 text-[14px] font-medium text-ink transition-colors hover:border-ink"
            >
              {t("product.call")}
              <span dir="ltr" className="font-normal text-muted">+90 537 798 11 29</span>
            </a>

            {/* Öne çıkan ürünün tanıtım sayfası: uygulama adımları, hesaplayıcı, sık sorulanlar */}
            {urun.vitrin && (
              <Link
                to={urun.vitrin}
                className="mt-6 flex items-center justify-between gap-4 rounded-xl border border-accent/30 bg-accent-soft px-5 py-4 text-start transition-colors hover:border-accent"
              >
                <span>
                  <span className="block text-[14px] font-semibold text-ink">{t("spotlight.detailCta")}</span>
                  <span className="mt-0.5 block text-[12.5px] text-body">{t("spotlight.detailCtaText")}</span>
                </span>
                <span aria-hidden="true" className="arrow-forward shrink-0 text-accent" />
              </Link>
            )}

            {/* Açıklama */}
            <div className="mt-10 border-t border-line pt-8">
              <h2 className="text-[15px] font-semibold text-ink">{t("product.description")}</h2>
              <p className="mt-3 text-[14.5px] leading-[1.75] text-body">
                {t(urun.marka ? "product.autoDescription" : "spotlight.autoDescriptionNoBrand", { ad: urun.ad, marka: urun.marka, kategori })}{" "}
                {boyutlar.length > 1 &&
                  t("product.optionsSentence", {
                    sayi: boyutlar.length,
                    // Uzun ölçü listelerinde ilk sekizi yeter; tamamı seçenek düğmelerinde duruyor
                    liste: boyutlar.length > 8 ? `${boyutlar.slice(0, 8).join(", ")} …` : boyutlar.join(", "),
                  })}{" "}
                {t("product.priceInfo")}
              </p>

              {ozellikler.length > 0 && (
                <ul className="mt-6 space-y-2.5">
                  {ozellikler.map((kod) => (
                    <li key={kod} className="flex items-start gap-3 text-[14px] leading-snug text-body">
                      <span aria-hidden="true" className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                      {ozellikMetni(t, kod)}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Güven unsurları */}
            <ul className="mt-10 grid gap-px overflow-hidden rounded-xl border border-line bg-line sm:grid-cols-3">
              {["wholesale", "since", "quote"].map((k) => (
                <li key={k} className="bg-white px-4 py-4 text-[12.5px] leading-snug text-body">
                  <span className="block text-[13px] font-semibold text-ink">
                    {t(`product.trust.${k}Title`)}
                  </span>
                  {t(`product.trust.${k}`)}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Tamamlayıcı ürünler */}
      {tamamlayanlar.length > 0 && (
        <section className="border-t border-line">
          <div className="container-page py-14">
            <div className="mb-8">
              <p className="eyebrow">{t("product.complementaryEyebrow")}</p>
              <h2 className="mt-2 text-[22px] font-semibold tracking-tight text-ink">
                {t("product.complementary")}
              </h2>
              <p className="mt-2 max-w-xl text-[14px] leading-relaxed text-muted">
                {t("product.complementaryText")}
              </p>
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {tamamlayanlar.map((u) => (
                <ProductCard key={u.id} urun={u} altBilgi={kategoriAdi(adlar, u.kategori, lang)} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Benzer ürünler */}
      {benzerler.length > 0 && (
        <section className="border-t border-line bg-canvas/50">
          <div className="container-page py-14">
            <div className="mb-8 flex items-end justify-between gap-4">
              <h2 className="text-[22px] font-semibold tracking-tight text-ink">
                {t("product.related")}
              </h2>
              <Link to={`/urunler/${urun.kategori}`} className="link-arrow text-[13px]">
                {t("product.viewAll")}
                <span aria-hidden="true" className="arrow-forward" />
              </Link>
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {benzerler.map((u) => (
                <ProductCard key={u.id} urun={u} altBilgi={kategoriAdi(adlar, u.kategori, lang)} />
              ))}
            </div>
          </div>
        </section>
      )}

      <SonIncelenenler haric={urun.id} />

      {/* Büyük görsel */}
      {buyukGorsel && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={urun.ad}
          onClick={() => setBuyukGorsel(false)}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-3xl overflow-hidden rounded-2xl bg-white"
          >
            <img src={urun.gorsel} alt={urun.ad} className="max-h-[80vh] w-full object-contain p-6 sm:p-10" />
            <p className="border-t border-line px-5 py-3 text-[13.5px] font-medium text-ink">{urun.ad}</p>
            <button
              type="button"
              onClick={() => setBuyukGorsel(false)}
              aria-label={t("product.close")}
              className="absolute end-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-ink text-white transition-opacity hover:opacity-80"
            >
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4" aria-hidden="true">
                <path d="M5 5l10 10M15 5L5 15" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
